/**
 * Выводит список игр для подборок, таких как:
 * "Мои игры", "Новинки", "Популярное", игры по жанрам
 * для авторизованного пользователя рендерится бесконечный скрол,
 * для неавторизоыанного - пагинация
 *
 * @param {(options: ICGIUrlOptions): Promise<void>} fetchApps - функция для загузки игр с сервера
 * @param {(options: ICGIUrlOptions): Promise<void>} fetchAppsNext - функция для догузки игр с сервера (load more)
 *
 * @param {number} total - общее кол-во игр в подборке
 * @param {number} hasMore - можно ли догрузить игры
 *
 * @param {IAppsListProps} appsListOptions - набор игр и правил для их отображения, такие как:
 * указывать ли в карточке с игрой жанр, заголовок, рейтинг итд
 * тип карточки, тип изобьражения, реф, методы для кастомизации списка и дропдауна
 */

import * as React from 'react';
import { withRouter } from 'react-router';
import { matchPath } from 'react-router-dom';

import { reaction } from 'mobx';
import { inject, observer } from 'mobx-react';
import InfiniteScroll from 'react-infinite-scroller';

import { catalogConfig, getConfig, URLS } from '@constants';

import { EAppPicSize, EAppsGenreEng, EContext, EResponseStatus, EStore } from '@enums';

import { counters, getPageTitle, translations } from '@utils';

import { ITranslatable, Translate, TranslateFn } from '@decorators';

import {
	AppsList,
	Breadcrumbs,
	CompilationDescription,
	EAppItemType,
	IBreadcrumb,
	SpinnerBottom,
	ErrorScreen,
	ErrorOfflineScreen,
} from '@components';

import { ContentWrapper, Pagination } from '@ui';

import { ICompilationPageProps, ICompilationPageState } from './types';

import * as s from './CompilationPage.scss';

@inject(EStore.CURRENT_USER, EStore.UI)
@observer
@Translate('components.AppsCatalog.CompilationPage')
export class CompilationPageView extends React.PureComponent<ICompilationPageProps, ICompilationPageState> implements ITranslatable {

	declare t: TranslateFn;

	static defaultProps = {
		total: 0,
		hasMore: false,
	};

	loadMoreOffset = 0;
	isInfinityScrollLoading = false;

	componentDidMount() {
		const { uiStore } = this.props;

		reaction(
			() => uiStore?.isOnline,
			// eslint-disable-next-line @typescript-eslint/no-misused-promises
			async (isOnline) => {
				if (isOnline && !this.props.appsListOptions?.apps?.length) {
					await this.fetchAppsWithConfigs();
				}
			},
		);
	}

	async componentDidUpdate(prevProps: ICompilationPageProps) {
		const {
			match: { params: { page, genre } },
			appsListOptions: { apps },
			currentUserStore: { isMyCreated },
		} = this.props;
		const { page: prevPage, genre: prevGenre } = prevProps.match.params;

		if ((page !== prevPage || genre !== prevGenre) && (!isMyCreated || !apps.length)) {
			await this.fetchAppsWithConfigs();
		}
	}

	getPageTitle = () => {
		const pathname = this.props.match?.url;

		return getPageTitle(
			pathname,
			(url: string) => Boolean(matchPath(pathname, url)),
		);
	};

	async fetchAppsWithConfigs() {
		const {
			match: { params: { page, genre } },
			currentUserStore: { isMyCreated },
			fetchApps,
		} = this.props;

		const limit = isMyCreated
			? catalogConfig.appsLimit.auth
			: catalogConfig.appsLimit.guest;

		await fetchApps({
			genre: genre as EAppsGenreEng || undefined,
			context: EContext.MOBILE,
			limit,
			offset: limit * (Number(page) - 1),
		});
	}

	loadMoreApps = async () => {
		const isMyCreated = this.props.currentUserStore.isMyCreated;
		const limit = isMyCreated
			? catalogConfig.appsLimit.auth
			: catalogConfig.appsLimit.guest;

		if (!this.isInfinityScrollLoading) {
			this.isInfinityScrollLoading = true;

			await this.props.fetchAppsNext({
				context: EContext.MOBILE,
				limit,
				offset: (limit * (this.loadMoreOffset + 1)),
			});

			this.loadMoreOffset += 1;
			this.isInfinityScrollLoading = false;
		}
	};

	onClick = () => {
		counters.sendRB(this.props.paginationCounter);
	};

	renderAppList() {
		const { appsListOptions } = this.props;
		const appsLimit = this.props.currentUserStore.isMyCreated
			? catalogConfig.appsLimit.auth
			: catalogConfig.appsLimit.guest;

		return (
			<AppsList
				{...appsListOptions}
				className={s.appItem}
				imageType={appsListOptions.imageType || EAppPicSize.PIC_143_113}
				type={appsListOptions.type || EAppItemType.HORIZONTAL}
				descriptionLength={appsListOptions.descriptionLength}
				appsLimitToRender={appsLimit}
				hasTitle={appsListOptions.hasTitle || true}
			/>
		);
	}

	render() {
		const {
			match: { params: { page } },
			total,
			hasMore,
			appsListOptions,
			paginationLimit,
			compilationDescription,
			currentUserStore: { isMyCreated },
			uiStore,
		} = this.props;
		const appsCatalogBasePath = getConfig().apps.pathname || URLS.APPS;
		const appsLimit = paginationLimit || isMyCreated
			? catalogConfig.appsLimit.auth
			: catalogConfig.appsLimit.guest;
		const isResponseError = appsListOptions.responseStatus === EResponseStatus.ERROR;
		const isResponseEmpty = !appsListOptions.apps?.length && appsListOptions?.responseStatus === EResponseStatus.OK;
		const appsList = this.renderAppList();
		const breadcrumbs: IBreadcrumb[]= [
			{
				title: translations.appCatalog('apps-all'),
				link: appsCatalogBasePath,
			},
			{
				title: this.getPageTitle().breadcrumb || '',
			},
		];

		if (isResponseError || isResponseEmpty) {
			return uiStore.isOnline
				? <ErrorScreen>{this.t('empty')}</ErrorScreen>
				: <ErrorOfflineScreen/>;
		}

		const isFirstPage = page === '1' || !page;

		return (
			<ContentWrapper className={s.block}>
				{
					isMyCreated
						? (
							/* eslint-disable @typescript-eslint/ban-ts-comment */
							// @ts-ignore
							<InfiniteScroll
								pageStart={0}
								loadMore={this.loadMoreApps}
								hasMore={hasMore}
								// key={0} - хак для InfiniteScroll, иначе ошибка
								loader={<SpinnerBottom key={0}/>}
								threshold={10}
							>
								{ appsList }
							</InfiniteScroll>
						)
						: (
							<>
								<div className={s.paginationContent}>
									{ appsList }
								</div>
								<Pagination
									currentPage={Number(page)}
									className={s.pagination}
									totalItems={total}
									limitOnPage={appsLimit}
									onClick={this.onClick}
									pagesToShowBeforeEllipses={3}
								/>
								{(Boolean(compilationDescription) && isFirstPage) &&
									<CompilationDescription>
										{compilationDescription}
									</CompilationDescription>
								}
								<Breadcrumbs
									className={s.breadcrumbs}
									breadcrumbs={breadcrumbs}
								/>
							</>
						)
				}
			</ContentWrapper>
		);
	}

}

export const CompilationPage = withRouter(CompilationPageView);
